/*
 * FICHIER: MaintenancePage.js
 * BUT: Afficher une page "Maintenance en cours" quand le backend est injoignable
 *
 * Style aligné sur la page countdown : image de fond stade de Compiègne, overlay sombre, typo blanche.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';

// Même image de fond que la page countdown : stade d'athlétisme de Compiègne
const BG_URL = 'https://oise24.fr/fileadmin/user_upload/oise24/les-centres-de-preparations-aux-jeux/athletisme/compiegne/athletisme-compiegne-stade-5.jpg';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: url(${BG_URL}) center center/cover no-repeat;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.65);
    z-index: 1;
  }
  @media (max-width: 700px) {
    background-position: center center;
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: #fff;
  max-width: 520px;
  padding: 0 20px;
  @media (max-width: 600px) {
    max-width: 95vw;
    padding: 0 10px;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: 2px;
  margin: 0 0 0.5em;
  color: #fff;
  line-height: 1.2;
  @media (max-width: 600px) {
    font-size: 1.6rem;
    letter-spacing: 1px;
  }
`;

const Message = styled.p`
  font-size: 1.2rem;
  font-weight: 400;
  margin: 0 0 1.5em;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.5;
  letter-spacing: 0.5px;
  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 14px 28px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
  background: #f7931e;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  letter-spacing: 1px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(247, 147, 30, 0.4);
    background: #ffa726;
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    opacity: 0.85;
    cursor: not-allowed;
    transform: none;
  }
`;

export default function MaintenancePage({ onRetry, isRetrying = false }) {
  const [retrying, setRetrying] = useState(false);
  const busy = isRetrying || retrying;

  const handleRetry = async () => {
    if (busy || !onRetry) return;
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Maintenance en cours - OffiSport</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Overlay>
        <Content>
          <Title>Maintenance en cours</Title>
          <Message>
            Le site est temporairement indisponible. Merci de réessayer dans quelques instants.
          </Message>
          {onRetry && (
            <RetryButton type="button" onClick={handleRetry} disabled={busy}>
              {busy ? 'Vérification…' : 'Réessayer'}
            </RetryButton>
          )}
        </Content>
      </Overlay>
    </>
  );
}
